import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

import { getDashboardPanelTokens, rem } from '../dashboardPanelTokens';
import type { CoverageGroup, CoverageLine } from './coverage.types';
import { rowFacts } from './coverageFacts';
import { getToneTokens } from './coverageTokens';
import { coverageHref } from './coverageLinks';
import {
  CoverageEyebrow,
  CoverageFacts,
  CoverageStatusPill,
} from './CoverageParts';

interface CoverageGroupRowProps {
  readonly group: CoverageGroup;
  readonly now?: string | number | Date;
}

/**
 * A product line that is not the hero: the same facts, scannable rather than
 * read. A row awaiting a decision keeps the quote's dashed edge and tint so it
 * cannot be mistaken for cover already in place.
 */
export function CoverageGroupRow({
  group,
  now = Date.now(),
}: Readonly<CoverageGroupRowProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);
  const caution = getToneTokens('caution', palette.mode);

  const line = getRowLine(group);

  if (!line) {
    return null;
  }

  const isQuote = line.kind === 'quote';

  return (
    <Box
      component="li"
      data-qa="COVERAGE_GROUP_ROW"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: `${rem(12)} ${rem(28)}`,
        padding: `${rem(14)} ${rem(4)}`,
        borderTop: `1px solid ${tokens.line}`,
        ...(isQuote
          ? {
              paddingLeft: rem(12),
              borderLeft: `${rem(3)} dashed ${caution.ink}99`,
              backgroundColor: caution.surface,
            }
          : {}),
      }}
    >
      <Box sx={{ flex: '1 1 auto', minWidth: rem(180) }}>
        <CoverageEyebrow kind={line.kind} compact />
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={rem(8)}
          sx={{ marginTop: rem(3) }}
        >
          <Typography
            sx={{ fontSize: rem(13), fontWeight: 700, color: tokens.ink }}
          >
            {group.productLabel}
          </Typography>
          <CoverageStatusPill status={line.status} compact />
        </Box>
        <Typography
          sx={{ marginTop: rem(2), fontSize: rem(12), color: tokens.inkFaint }}
        >
          {line.reference || '—'}
        </Typography>
      </Box>

      <CoverageFacts facts={rowFacts(line, now)} compact />

      <Typography
        component={Link}
        to={coverageHref(line)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: rem(5),
          flexShrink: 0,
          fontSize: rem(13),
          fontWeight: 600,
          color: tokens.brand,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {isQuote ? 'Review & bind' : 'View policy'}
        <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: rem(11) }} />
      </Typography>
    </Box>
  );
}

const getRowLine = (group: CoverageGroup): CoverageLine | undefined =>
  group.current ?? group.lapsed ?? group.quotes[0];
